import { useCallback, useMemo } from 'react';
import type { QueryKey } from '@tanstack/react-query';

import {
  automationGetArtifactContent,
  getAutomationGetActivePlanQueryKey,
  getAutomationGetArtifactContentQueryKey,
  useAutomationGetEvents,
  useAutomationSendMessage,
  useAutomationSendToolCall,
  useAutomationSendToolResult,
  useAutomationStop,
  useAutomationWake,
} from '@/generated/hooks/rain/v1/automation_scoped/thread_service';
import type {
  ThreadServiceSendMessageBody,
  ThreadServiceSendToolCallBody,
  ThreadServiceSendToolResultBody,
} from '@/generated/hooks/rain/models';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';

import type { IRainAdapterParts } from './buildRainAdapter';

/** Everything `IRainAdapterParts` needs except the per-agent behavioral config. */
export type TAutomationRainAdapterParts = Omit<
  IRainAdapterParts,
  'agentConfig'
>;

/**
 * The automation-scoped half of a Rain thread adapter: the backlog query, the
 * thread mutations, the SSE URL, the upload scope, and the plan/artifact
 * resolvers, all bound to
 * `…/automations/{automation}/agents/{agentId}/threads/{thread}/…`.
 *
 * The peer of `useWorkspaceRainAdapterParts` for the scope that hangs threads
 * off an automation, so an agent on it reduces to an `agentConfig`.
 *
 * `sendToolCall` is wired: the editor's Test button injects one. `hermes` is on
 * this same scope and hand-rolls these parts rather than reusing them, to keep
 * `sendToolCall` off a surface with no equivalent action.
 */
export function useAutomationRainAdapterParts(
  automationId: string,
  agentId: string,
  /**
   * Undefined while the thread is still being created. The chat renders before
   * its thread exists — sending from the drafts hero lands on the editor
   * immediately and the thread arrives a beat later — so every thread-scoped
   * call here has to be inert until it is present, not merely tolerant of a
   * blank id. The stream URL below was already written that way; the events
   * query needed the matching `enabled`.
   */
  threadId: string | undefined,
): TAutomationRainAdapterParts {
  const organization = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspace = useUserPreferencesStore(selectCurrentWorkspaceId);

  const events = useAutomationGetEvents(
    organization,
    workspace,
    automationId,
    agentId,
    // Never requested without a thread — `enabled` below is the real guard, so
    // this placeholder is only here to satisfy the generated signature.
    threadId ?? '',
    { compacted: false },
    {
      query: {
        enabled: Boolean(threadId),
        refetchOnMount: 'always',
        refetchOnWindowFocus: false,
      },
    },
  );

  const { mutateAsync: sendMessageAsync } = useAutomationSendMessage();
  const { mutateAsync: sendToolResultAsync } = useAutomationSendToolResult();
  const { mutateAsync: sendToolCallAsync } = useAutomationSendToolCall();
  const { mutateAsync: stopAsync } = useAutomationStop();
  const { mutateAsync: wakeAsync } = useAutomationWake();

  // Null until the thread exists, rather than an object carrying a blank id.
  // Every mutation below is thread-scoped, so a blank id would produce a
  // malformed POST that looks like a server fault; `requireThread` turns the
  // same mistake into a stack trace pointing at the caller.
  const thread = useMemo(
    () =>
      threadId
        ? {
            organization,
            workspace,
            automation: automationId,
            agentId,
            threadId,
          }
        : null,
    [agentId, automationId, organization, threadId, workspace],
  );

  const requireThread = useCallback(() => {
    if (!thread) {
      throw new Error(
        'Rain adapter: a thread-scoped action was called before the thread existed. The composer is disabled until it does — see useRainThread.',
      );
    }
    return thread;
  }, [thread]);

  const sendMessage = useCallback(
    (data: ThreadServiceSendMessageBody) =>
      sendMessageAsync({ ...requireThread(), data }),
    [sendMessageAsync, requireThread],
  );

  const sendToolResult = useCallback(
    (data: ThreadServiceSendToolResultBody) =>
      sendToolResultAsync({ ...requireThread(), data }),
    [sendToolResultAsync, requireThread],
  );

  const sendToolCall = useCallback(
    (data: ThreadServiceSendToolCallBody) =>
      sendToolCallAsync({ ...requireThread(), data }).then(
        response => response.data,
      ),
    [sendToolCallAsync, requireThread],
  );

  const stop = useCallback(
    () => stopAsync({ ...requireThread(), data: {} }),
    [stopAsync, requireThread],
  );

  const wake = useCallback(
    () => wakeAsync({ ...requireThread(), data: {} }),
    [requireThread, wakeAsync],
  );

  // Empty until every id is present so the core hook never opens a malformed
  // stream.
  const streamUrl =
    organization && workspace && automationId && threadId
      ? `/api/v1/organizations/${organization}/workspaces/${workspace}/automations/${automationId}/agents/${agentId}/threads/${threadId}/stream`
      : '';

  const getActivePlanQueryKey = useCallback(
    (): QueryKey =>
      getAutomationGetActivePlanQueryKey(
        organization,
        workspace,
        automationId,
        agentId,
        threadId,
      ),
    [agentId, automationId, organization, threadId, workspace],
  );

  const fetchArtifactContent = useCallback(
    (artifactUri: string) =>
      automationGetArtifactContent(
        organization,
        workspace,
        automationId,
        agentId,
        // Artifacts only exist once a turn has produced them, so there is
        // always a thread by the time anything asks for one.
        requireThread().threadId,
        encodeURIComponent(artifactUri),
      ),
    [agentId, automationId, organization, requireThread, workspace],
  );

  const getArtifactContentQueryKey = useCallback(
    (artifactUri: string): QueryKey =>
      getAutomationGetArtifactContentQueryKey(
        organization,
        workspace,
        automationId,
        agentId,
        threadId,
        encodeURIComponent(artifactUri),
      ),
    [agentId, automationId, organization, threadId, workspace],
  );

  return {
    agentId,
    events: {
      data: events.data,
      isFetching: events.isFetching,
      error: events.error,
    },
    sendMessage,
    sendToolResult,
    sendToolCall,
    stop,
    wake,
    streamUrl,
    attachmentScope: `automations/${automationId}`,
    getActivePlanQueryKey,
    fetchArtifactContent,
    getArtifactContentQueryKey,
  };
}
